export type SeoPageId =
  | 'home'
  | 'archive'
  | 'privacy'
  | 'terms'
  | 'about'
  | 'contact'
  | 'faq'
  | 'school'
  | 'schoolSnack'
  | 'season'
  | 'care'
  | 'notFound';

export type PageSeo = {
  title: string;
  description: string;
  path: string;
  keywords: string;
  robots?: string;
};

/** sitemap.xml · canonical · OG 에 쓰는 운영 도메인 (끝 슬래시 없음) */
export const SITE_ORIGIN = 'https://5b2e.co.kr';

export const SITE_NAME = '오병이어푸드';
export const SITE_NAME_LEGAL = '(주)오병이어푸드';
export const SITE_NAME_CORP = '주식회사 오병이어푸드';
export const SITE_PHONE = '02-2060-3716';
export const SITE_EMAIL = 'munwon98@naver.com';
export const SITE_ADDRESS = '경기도 광명시 안양천로 502번길 15 광명테크노타운 303호';
export const DEFAULT_OG_IMAGE = '/assets/images/hero1.jpg';
export const DEFAULT_KEYWORDS =
  '오병이어푸드, 학교급식, 떡, 급식떡, 돌봄간식, HACCP, 전통식품, 경기미, 광명';

export const DEFAULT_DESCRIPTION =
  '정직과 신뢰를 바탕으로 건강한 급식문화를 만들어가는 학교급식 전문 떡 제조기업 주식회사 오병이어푸드';

export const PAGE_SEO: Record<SeoPageId, PageSeo> = {
  home: {
    title: '오병이어푸드 | 학교급식 전문 떡 제조기업',
    description: DEFAULT_DESCRIPTION,
    path: '/',
    keywords: DEFAULT_KEYWORDS,
  },
  about: {
    title: '회사소개 | 오병이어푸드',
    description:
      '2005년 설립된 오병이어푸드는 학교급식 전문 떡을 생산하며, 정직한 원료와 깨끗한 생산환경으로 안전한 먹거리를 제공합니다.',
    path: '/about/',
    keywords: `${DEFAULT_KEYWORDS}, 회사소개, 오병이어`,
  },
  contact: {
    title: '제휴 및 문의 | 오병이어푸드',
    description: '오병이어푸드 제휴·납품·제품 문의. 학교급식·돌봄간식 관련 상담을 남겨 주세요.',
    path: '/contact/',
    keywords: `${DEFAULT_KEYWORDS}, 제휴, 문의, 납품`,
  },
  archive: {
    title: '자료실 | 오병이어푸드',
    description: '오병이어푸드 공지사항과 자료실입니다. 제품·인증·급식 관련 자료를 확인할 수 있습니다.',
    path: '/board/',
    keywords: `${DEFAULT_KEYWORDS}, 자료실, 공지사항`,
  },
  faq: {
    title: 'FAQ | 오병이어푸드',
    description: '오병이어푸드 자주 묻는 질문입니다. 제품, 주문, 인증에 대한 안내를 확인하세요.',
    path: '/faq/',
    keywords: `${DEFAULT_KEYWORDS}, FAQ, 자주묻는질문`,
  },
  school: {
    title: '학교급식 조리용 | 오병이어푸드',
    description: '조리 시 식감과 색감을 살린 오병이어푸드 학교급식 조리용 떡 제품 안내입니다.',
    path: '/school/',
    keywords: `${DEFAULT_KEYWORDS}, 학교급식 조리용, 가래떡, 떡볶이`,
  },
  schoolSnack: {
    title: '학교급식 후식(간식)용 | 오병이어푸드',
    description: '아이들이 좋아하는 오병이어푸드 학교급식 후식·간식용 떡 제품 안내입니다.',
    path: '/school/snack/',
    keywords: `${DEFAULT_KEYWORDS}, 학교급식 후식, 간식용 떡, 설기`,
  },
  season: {
    title: '절기별 추천떡 | 오병이어푸드',
    description: '명절과 절기에 맞춘 오병이어푸드 추천 떡 구성입니다.',
    path: '/season/',
    keywords: `${DEFAULT_KEYWORDS}, 절기떡, 명절떡, 송편`,
  },
  care: {
    title: '돌봄간식 | 오병이어푸드',
    description: '학교·돌봄교실에 적합한 오병이어푸드 돌봄간식 떡 제품 안내입니다.',
    path: '/care/',
    keywords: `${DEFAULT_KEYWORDS}, 돌봄간식, 돌봄급식`,
  },
  privacy: {
    title: '개인정보처리방침 | 오병이어푸드',
    description: '주식회사 오병이어푸드 개인정보처리방침입니다.',
    path: '/privacy/',
    keywords: '오병이어푸드, 개인정보처리방침',
  },
  terms: {
    title: '이용약관 | 오병이어푸드',
    description: '주식회사 오병이어푸드 웹사이트 이용약관입니다.',
    path: '/terms/',
    keywords: '오병이어푸드, 이용약관',
  },
  notFound: {
    title: '페이지를 찾을 수 없습니다 | 오병이어푸드',
    description: '요청하신 페이지를 찾을 수 없습니다.',
    path: '/404/',
    keywords: SITE_NAME,
    robots: 'noindex, follow',
  },
};

export const SITEMAP_PAGES: SeoPageId[] = [
  'home',
  'about',
  'contact',
  'school',
  'schoolSnack',
  'season',
  'care',
  'archive',
  'faq',
];

/** `/school/snack/` → schoolSnack 처럼 앱 경로를 SEO 페이지 키로 변환 */
export function seoPageFromAppPath(appPath: string): SeoPageId {
  const parts = appPath.replace(/\/+$/, '').split('/').filter(Boolean);
  const [segment, child] = parts;

  if (!segment || segment === 'index.html') return 'home';

  switch (segment) {
    case 'about':
      return 'about';
    case 'contact':
      return 'contact';
    case 'board':
    case 'archive':
    case 'notice':
      return 'archive';
    case 'faq':
      return 'faq';
    case 'school':
      return child === 'snack' ? 'schoolSnack' : 'school';
    case 'season':
      return 'season';
    case 'care':
      return 'care';
    case 'privacy':
      return 'privacy';
    case 'terms':
      return 'terms';
    case '404':
    case '404.html':
      return 'notFound';
    default:
      return 'notFound';
  }
}
